import React, { useEffect, useRef, useState } from 'react';
import { projects } from '../data/mockData';
import { ExternalLink, Folder, ArrowUpRight } from 'lucide-react';
import { Badge } from './ui/badge';

const ProjectCard = ({ project, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="group h-full p-6 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 hover:border-emerald-200 dark:hover:border-emerald-500/20 transition-colors duration-300">
        <div className="flex items-start justify-between mb-4">
          <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-emerald-500/10">
            <Folder className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-400 hover:text-emerald-500 transition-colors duration-200"
            aria-label={`View ${project.name} on GitHub`}
          >
            <ArrowUpRight className="h-5 w-5" />
          </a>
        </div>

        <span className="inline-block text-xs font-mono text-emerald-600 dark:text-emerald-400 mb-2">
          {project.category}
        </span>

        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-200">
          {project.name}
        </h3>

        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
          {project.description}
        </p>

        <div className="flex flex-wrap gap-2 mt-auto">
          {project.tech.map((t) => (
            <Badge
              key={t}
              variant="secondary"
              className="bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 border-0 text-xs font-normal"
            >
              {t}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProjectsSection = () => {
  return (
    <section id="projects" className="py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-12">
          <div className="h-px w-12 bg-emerald-500"></div>
          <h2 className="font-mono text-sm uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
            Projects
          </h2>
        </div>

        <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Things I've Built
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-12 max-w-xl">
          A selection of projects that showcase my skills in full-stack development, API design, and creative problem-solving.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;